/** Dialogs for MY ACTION CENTRE — assign / comment / due date / priority / escalate / snooze-until. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const TITLES = { assign: "Assign", comment: "Comment", due: "Set due date", priority: "Change priority", escalate: "Escalate", snooze: "Snooze until" };
const PRIOS = ["critical", "high", "medium", "low"];

export function ActionDialog({ dialog, onClose, onDone }) {
  const kind = dialog?.kind; const item = dialog?.item;
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dialog) return;
    setText(""); setDate(item?.due_at || "");
    if (kind === "assign") api.get("/one-today/assignees").then(({ data }) => setUsers(data.items || [])).catch(() => {});
    if (kind === "comment") api.get("/action-centre/comments", { params: { key: item.key } }).then(({ data }) => setComments(data.items || [])).catch(() => {});
  }, [dialog, kind, item]);

  const submit = async (fn, msg) => {
    setSaving(true);
    try { await fn(); toast.success(msg); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ac-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{TITLES[kind]}: <span className="text-slate-500 font-normal">{item?.title}</span></DialogTitle>
        </DialogHeader>

        {kind === "assign" && (
          <div className="space-y-2">
            {item?.assign_history?.length > 0 && (
              <div className="text-[11px] text-slate-400 space-y-0.5">
                {item.assign_history.map((h, i) => <div key={i}>→ {h.assignee_email} by {h.assigned_by} on {(h.at || "").slice(0, 10)}</div>)}
              </div>
            )}
            <div className="max-h-64 overflow-y-auto grid gap-1">
              {users.map((u) => (
                <button key={u.email} disabled={saving} data-testid={`assignee-${u.email}`}
                  onClick={() => submit(() => api.post("/one-today/assign", { key: item.key, assignee_email: u.email, title: item.title }), `Assigned to ${u.email}`)}
                  className="flex items-center gap-2 text-left rounded-md border border-slate-100 hover:bg-slate-50 px-3 py-2">
                  <span className="text-sm text-slate-800">{u.name || u.email}</span>
                  <span className="text-[11px] text-slate-400 ml-auto">{u.role}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {kind === "comment" && (
          <div className="space-y-3">
            <div className="max-h-48 overflow-y-auto space-y-1.5" data-testid="ac-comment-list">
              {comments.length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
              {comments.map((c) => (
                <div key={c.id} className="text-[12px] rounded bg-slate-50 border border-slate-100 px-2.5 py-1.5">
                  <span className="font-medium text-slate-700">{(c.by || "").split("@")[0]}</span>
                  <span className="text-slate-400 ml-2 font-mono text-[10px]">{(c.at || "").slice(0, 16).replace("T", " ")}</span>
                  <div className="text-slate-700 mt-0.5">{c.text}</div>
                </div>
              ))}
            </div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" rows={3} data-testid="ac-comment-input" />
            <Button disabled={saving || !text.trim()} data-testid="ac-comment-submit"
              onClick={() => submit(() => api.post("/action-centre/comment", { key: item.key, text }), "Comment added")}>Add comment</Button>
          </div>
        )}

        {(kind === "due" || kind === "snooze") && (
          <div className="space-y-3">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ac-date-input"
              className="border border-slate-200 rounded px-2 py-1.5 text-sm w-full" />
            <div className="flex gap-2">
              <Button disabled={saving || !date} data-testid="ac-date-submit"
                onClick={() => submit(() => kind === "due"
                  ? api.post("/action-centre/due", { key: item.key, due_at: date })
                  : api.post("/one-today/snooze", { key: item.key, until: date }),
                  kind === "due" ? `Due date set to ${date}` : `Snoozed until ${date}`)}>
                {kind === "due" ? "Set due date" : "Snooze"}
              </Button>
              {kind === "due" && item?.due_overridden && (
                <Button variant="outline" disabled={saving} data-testid="ac-date-clear"
                  onClick={() => submit(() => api.post("/action-centre/due", { key: item.key, due_at: null }), "Due date override cleared")}>Clear override</Button>
              )}
            </div>
          </div>
        )}

        {kind === "priority" && (
          <div className="grid grid-cols-2 gap-2">
            {PRIOS.map((p) => (
              <button key={p} disabled={saving} data-testid={`ac-priority-${p}`}
                onClick={() => submit(() => api.post("/action-centre/priority", { key: item.key, priority: p }), `Priority set to ${p}`)}
                className={`rounded-md border px-3 py-2 text-sm capitalize ${item?.priority === p ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:bg-slate-50"}`}>{p}</button>
            ))}
          </div>
        )}

        {kind === "escalate" && (
          <div className="space-y-3">
            <p className="text-[12px] text-slate-500">Escalation marks this item critical, records it in the Exception / Escalation Centre and notifies management.</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Why is this being escalated? (required)" rows={3} data-testid="ac-escalate-reason" />
            <Button variant="destructive" disabled={saving || !text.trim()} data-testid="ac-escalate-submit"
              onClick={() => submit(() => api.post("/action-centre/escalate", { key: item.key, reason: text, title: item.title, customer: item.customer, route: item.route }), "Escalated — management notified")}>Escalate</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
